import { ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import api from '@/services/api'
import { usePlayer, syncPlayer, player } from '@/composables/game/player'
import { useToaster } from '@/composables/game/toasters'

const showPlantMineModal = ref(false)
const showMineTutorialModal = ref(false)
const showMineInfoModal = ref(false)
const showUseMagnetOverlay = ref(false)
const showMinesCollectedModal = ref(false)
const collectedMinesCount = ref(0)
const hasSeenMineTutorial = useLocalStorage(buildDropeeStorageKey('mine_tutorial_seen'), false)

export function useMultiplayerMine() {
  const { updatePlayer, minesCount, canUseMines } = usePlayer()
  const { notify } = useToaster()

  async function plantMine(count: number = 1) {
      if (!canUseMines.value) return
      const result = await api.multiplayerTargetAction({ action: { type: 'mine', count: count } })
      if (result.success) {
        if (result.playerStats) {
          updatePlayer(result.playerStats)
        } else {
          await syncPlayer()
        }
        const targets = result.targetedPlayers
        if (targets && targets.length > 0) {
          for (const target of targets) {
            notify({
              type: 'success',
              title: 'Mine placed!',
              description: `Mine placed on ${target.firstName}'s card`,
            })
          }
        } else {
          notify({
            type: 'success',
            title: 'Mine placed!',
            description: "Mine placed on a random player's card",
          })
        }
      }
  }

  function openPlantMineModal() {
    if (!canUseMines.value) return
    showPlantMineModal.value = true
  }

  function closePlantMineModal() {
    showPlantMineModal.value = false
  }

  function openMineTutorialModal() {
    showMineTutorialModal.value = true
  }

  function closeMineTutorialModal() {
    showMineTutorialModal.value = false
  }

  function openMineInfoModal() {
    if (minesCount.value === 0) return
    showMineInfoModal.value = true
  }

  function closeMineInfoModal() {
    showMineInfoModal.value = false
  }

  function openUseMagnetOverlay() {
    if (minesCount.value === 0) return
    if (((player.value.specialCurrencies as Record<string, number>)['mp.mineMagnet'] ?? 0) === 0) return
    showUseMagnetOverlay.value = true
  }

  function closeUseMagnetOverlay() {
    showUseMagnetOverlay.value = false
  }

  function openMinesCollectedModal(count: number) {
    collectedMinesCount.value = count
    showMinesCollectedModal.value = true
  }

  function closeMinesCollectedModal() {
    showMinesCollectedModal.value = false
  }

  async function useMagnet() {
    const result = await api.multiplayerScanForMines()
    if (result.success) {
      updatePlayer(result.playerStats)
      await syncPlayer(true)
    }
    return result
  }

  return {
    showPlantMineModal,
    openPlantMineModal,
    closePlantMineModal,
    showMineTutorialModal,
    openMineTutorialModal,
    closeMineTutorialModal,
    hasSeenMineTutorial,
    plantMine,
    showMineInfoModal,
    openMineInfoModal,
    closeMineInfoModal,
    showUseMagnetOverlay,
    openUseMagnetOverlay,
    closeUseMagnetOverlay,
    useMagnet,
    showMinesCollectedModal,
    collectedMinesCount,
    openMinesCollectedModal,
    closeMinesCollectedModal,
  }
}
